import React, { useCallback, useEffect, useState } from 'react';
import {
  FocusContext,
  getCurrentFocusKey,
  setFocus,
  useFocusable
} from '@noriginmedia/norigin-spatial-navigation';

import useFeatureIntros from '@smart-tv/hooks/use-feature-intros';
import type { FeatureIntro as FeatureIntroData } from '@smart-tv/typings/content';

import FeatureIntroStep, { getFeatureIntroButtonFocusKey } from './feature-intro-step';
import styles from './feature-intro.module.scss';

interface Props {
  steps: FeatureIntroData['steps'];
  onClose: () => void;
  player?: boolean;
}

const FeatureIntroSlider: React.FC<Props> = (props: Props) => {
  const { steps, onClose } = props;
  const { markFeatureIntroAsSeen } = useFeatureIntros();

  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [navigatedBack, setNavigatedBack] = useState(false);

  const { ref, focusKey, hasFocusedChild } = useFocusable({
    focusKey: 'feature-intro-dialog',
    isFocusBoundary: true,
    trackChildren: true
  });

  const isFirstStep = activeStepIndex === 0;
  const isLastStep = activeStepIndex === steps.length - 1;
  // Tilbage er disabled på første trin, så fokus sættes på Næste knap der
  const primaryButton = navigatedBack && !isFirstStep ? 'back' : isLastStep ? 'close' : 'next';
  const primaryFocusKey = getFeatureIntroButtonFocusKey(steps[activeStepIndex].id, primaryButton);

  useEffect(() => {
    setFocus(primaryFocusKey);
  }, [primaryFocusKey]);

  useEffect(() => {
    if (hasFocusedChild) return;

    const currentFocusKey = getCurrentFocusKey();
    if (currentFocusKey && currentFocusKey.indexOf('error-dialog') === 0) return;

    setFocus(primaryFocusKey);
  }, [hasFocusedChild, primaryFocusKey]);

  const handleNext = useCallback(() => {
    setNavigatedBack(false);
    setActiveStepIndex((index) => Math.min(index + 1, steps.length - 1));
  }, [steps.length]);

  const handleBack = useCallback(() => {
    setNavigatedBack(true);
    setActiveStepIndex((index) => Math.max(index - 1, 0));
  }, []);

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        ref={ref}
        className={`${styles['feature-intro__card']} ${
          props.player ? styles['feature-intro__card--player'] : ''
        }`}
      >
        <div className={styles['feature-intro__viewport']}>
          <div
            className={styles['feature-intro__track']}
            style={{ transform: `translateX(-${activeStepIndex * 100}%)` }}
          >
            {steps.map((step, index) => (
              <FeatureIntroStep
                key={step.id}
                step={step}
                stepNumber={index + 1}
                totalSteps={steps.length}
                isActive={index === activeStepIndex}
                player={props.player}
                onNext={() => {
                  props.player && markFeatureIntroAsSeen(steps[index + 1].id);
                  handleNext();
                }}
                onBack={handleBack}
                onClose={onClose}
              />
            ))}
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  );
};

export default FeatureIntroSlider;
